/* McCarthy Family Reunion 2027 — mccarthy.js
   Vanilla JS: countdown, header scroll, cart drawer, modals,
   Green Book search/filter, Business Directory filter.
   No build step required. */

'use strict';

// ─── Countdown ───────────────────────────────────────────────────────────────
const _cdEl = document.querySelector('.mc-countdown');
const MC_TARGET = new Date((_cdEl && _cdEl.dataset.target) || '2027-07-17T17:00:00-04:00').getTime();

function mcCountdown() {
  const els = {
    days:    document.getElementById('mc-cd-days'),
    hours:   document.getElementById('mc-cd-hours'),
    minutes: document.getElementById('mc-cd-minutes'),
    seconds: document.getElementById('mc-cd-seconds'),
  };
  if (!els.days) return;

  function tick() {
    const ms   = Math.max(0, MC_TARGET - Date.now());
    const days = Math.floor(ms / 86400000);
    const hrs  = Math.floor((ms % 86400000) / 3600000);
    const min  = Math.floor((ms % 3600000) / 60000);
    const sec  = Math.floor((ms % 60000) / 1000);
    els.days.textContent    = days;
    els.hours.textContent   = String(hrs).padStart(2, '0');
    els.minutes.textContent = String(min).padStart(2, '0');
    els.seconds.textContent = String(sec).padStart(2, '0');
  }
  tick();
  setInterval(tick, 1000);
}

// ─── Header scroll shrink ─────────────────────────────────────────────────────
function mcHeaderScroll() {
  const header = document.querySelector('.mc-header');
  if (!header) return;
  const onScroll = () => header.classList.toggle('mc-header--scrolled', window.scrollY > 20);
  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();
}

// ─── Mobile nav toggle ────────────────────────────────────────────────────────
function mcMobileNav() {
  const btn   = document.getElementById('mc-nav-toggle');
  const panel = document.getElementById('mc-nav-panel');
  if (!btn || !panel) return;

  btn.addEventListener('click', () => {
    const open = panel.classList.toggle('is-open');
    btn.setAttribute('aria-expanded', String(open));
    document.body.style.overflow = open ? 'hidden' : '';
  });

  /* Accordion groups (Heritage, Resources) */
  panel.querySelectorAll('.mc-nav-panel__group-btn').forEach(groupBtn => {
    groupBtn.addEventListener('click', () => {
      const sub      = groupBtn.nextElementSibling;
      const expanded = groupBtn.getAttribute('aria-expanded') === 'true';
      groupBtn.setAttribute('aria-expanded', String(!expanded));
      sub.classList.toggle('is-open', !expanded);
    });
  });

  /* Close panel on link/action clicks — exclude accordion buttons */
  panel.querySelectorAll('a, button:not(.mc-nav-panel__group-btn)').forEach(el => {
    el.addEventListener('click', () => {
      panel.classList.remove('is-open');
      btn.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
    });
  });
}

// ─── Modal helpers ─────────────────────────────────────────────────────────────
function mcOpenModal(id) {
  const el = document.getElementById(id);
  if (!el) return;
  el.classList.add('is-open');
  document.body.style.overflow = 'hidden';
  el.addEventListener('keydown', mcTrapFocus);
  setTimeout(() => el.querySelector('[autofocus], input, button')?.focus(), 50);
}

function mcCloseModal(id) {
  const el = document.getElementById(id);
  if (!el) return;
  el.classList.remove('is-open');
  if (!document.querySelector('.mc-modal-veil.is-open, .mc-cart-drawer.is-open')) {
    document.body.style.overflow = '';
  }
  el.removeEventListener('keydown', mcTrapFocus);
}

function mcTrapFocus(e) {
  if (e.key !== 'Tab') return;
  const modal = e.currentTarget;
  const focusable = modal.querySelectorAll('a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])');
  const first = focusable[0];
  const last  = focusable[focusable.length - 1];
  if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
  else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
}

function mcModals() {
  // Close on veil click (unless drag started inside modal)
  document.querySelectorAll('.mc-modal-veil').forEach(veil => {
    let dragStartedInside = false;
    veil.addEventListener('mousedown', e => { dragStartedInside = !!e.target.closest('.mc-modal'); });
    veil.addEventListener('click', e => {
      if (!e.target.closest('.mc-modal') && !dragStartedInside) {
        const id = veil.id;
        if (id) mcCloseModal(id);
      }
      dragStartedInside = false;
    });
  });

  // Escape key
  document.addEventListener('keydown', e => {
    if (e.key !== 'Escape') return;
    document.querySelectorAll('.mc-modal-veil.is-open').forEach(el => {
      mcCloseModal(el.id);
    });
    mcCloseCart();
  });

  // Close buttons
  document.querySelectorAll('[data-mc-close]').forEach(btn => {
    btn.addEventListener('click', () => {
      const target = btn.dataset.mcClose;
      mcCloseModal(target);
    });
  });

  // Open buttons
  document.querySelectorAll('[data-mc-open]').forEach(btn => {
    btn.addEventListener('click', () => {
      const target = btn.dataset.mcOpen;
      mcOpenModal(target);
    });
  });
}

// ─── Contact modal form ───────────────────────────────────────────────────────
function mcContactForm() {
  const form = document.getElementById('mc-contact-form');
  if (!form) return;
  // Capture phase so we run before Shopify's recaptcha handler.
  // On error: block everything. On valid: do nothing — Shopify handles
  // recaptcha + native submission; success state is shown via return_to redirect.
  form.addEventListener('submit', e => {
    const errs = validateForm(form, ['contact[name]', 'contact[email]', 'contact[body]']);
    if (Object.keys(errs).length) {
      e.preventDefault();
      e.stopImmediatePropagation();
      showErrors(form, errs);
    }
  }, true);
}

// ─── Event registration forms ─────────────────────────────────────────────────
function mcEventForms() {
  document.querySelectorAll('.mc-register-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const eventId = btn.dataset.event;
      mcOpenModal('mc-reg-' + eventId);
    });
  });

  document.querySelectorAll('.mc-event-form').forEach(form => {
    form.addEventListener('submit', e => {
      // Validate required fields
      const required = Array.from(form.querySelectorAll('[required]')).map(el => el.name || el.id).filter(Boolean);
      const errs = validateForm(form, required);
      if (Object.keys(errs).length) {
        e.preventDefault();
        e.stopImmediatePropagation();
        showErrors(form, errs);
        return;
      }

      // Assemble the contact[body] field for Shopify email delivery
      const bodyField = form.querySelector('[name="contact[body]"]');
      if (bodyField) {
        const get = n => (form.querySelector(`[name="${n}"]`)?.value || '').trim();
        const eventType  = get('event_type') || 'standard';
        const eventTitle = get('contact[subject]');

        let body = `${eventTitle}\n\n`;
        body += `Name: ${get('firstName')} ${get('lastName')}\n`;
        body += `Email: ${get('contact[email]')}\n`;

        if (eventType === 'golf') {
          body += `ID#: ${get('family_id') || 'Not provided'}\n`;
          body += `Handicap: ${get('handicap') || 'Not provided'}\n`;
          body += `Notes: ${get('notes') || 'None'}`;
        } else if (eventType === 'banquet') {
          body += `ID#: ${get('family_id') || 'Not provided'}\n`;
          body += `Adults attending: ${get('adults') || '1'}\n`;
          body += `Children attending: ${get('children') || '0'}\n`;
          body += `Notes: ${get('notes') || 'None'}`;
        } else if (eventType === 'kids') {
          body += `Phone: ${get('phone')}\n`;
          body += `ID#: ${get('family_id') || 'Not provided'}\n`;
          const kids = [];
          form.querySelectorAll('.mc-kid-row').forEach((row, i) => {
            const name = (row.querySelector('[name$="[name]"]')?.value || '').trim();
            const age  = (row.querySelector('[name$="[age]"]')?.value  || '').trim();
            if (name) kids.push(`  Child ${i + 1}: ${name}${age ? ', age ' + age : ''}`);
          });
          body += `Children:\n${kids.length ? kids.join('\n') : '  None listed'}`;
        } else if (eventType === 'talent') {
          body += `ID#: ${get('family_id') || 'Not provided'}\n`;
          body += `Act name: ${get('act_name') || 'Not provided'}\n`;
          body += `Act type: ${get('act_type') || 'Not provided'}\n`;
          body += `Duration: ${get('duration') || 'Not provided'}\n`;
          body += `Description: ${get('description') || 'None'}\n`;
          body += `Special requirements: ${get('requirements') || 'None'}`;
        } else if (eventType === 'raffle') {
          body += `ID#: ${get('family_id') || 'Not provided'}\n`;
          body += `Item: ${get('item_description') || 'Not provided'}\n`;
          body += `Value: ${get('item_value') || 'Not provided'}\n`;
          body += `Condition: ${get('item_condition') || 'Not provided'}\n`;
          body += `Notes: ${get('notes') || 'None'}`;
        } else {
          body += `ID#: ${get('family_id') || 'Not provided'}\n`;
          body += `Number attending: ${get('attendees') || '1'}\n`;
          body += `Notes: ${get('notes') || 'None'}`;
        }

        bodyField.value = body;
      }
      // Valid — Shopify handles recaptcha + submission; success shown via return_to redirect
    }, true);
  });
}

// ─── Green Book search / sort ────────────────────────────────────────────────
// Data is loaded live from a published Google Sheets CSV.
// The URL is stored in the Green Book section setting "data_url" and injected
// by greenbook.liquid as window.MC_GB_DATA_URL. To update the data, edit the
// Google Sheet that's published to the web — changes appear on the site within
// a few minutes (Google's publish cache). No file upload needed.

function mcGreenBook() {
  const table  = document.getElementById('mc-gb-tbody');
  const search = document.getElementById('mc-gb-search');
  const count  = document.getElementById('mc-gb-count');
  const total  = document.getElementById('mc-gb-total');
  const pgEl   = document.getElementById('mc-gb-pagination');
  if (!table) return;

  const PER_PAGE = 50;
  let sortKey = 'id', sortDir = 'asc', page = 1;
  let gbData = [], minSegs = 3, activeBranch = null;

  // Columns that get a section-divider right border
  const SECTION_COLS = ['id', 'died', 'spouseDied'];

  function esc(s) { return String(s || '').replace(/</g, '&lt;').replace(/>/g, '&gt;'); }

  function render() {
    const q = (search?.value || '').trim().toLowerCase();

    let rows = gbData.filter(p => {
      if (activeBranch && String(p.id || '').split('.')[0] !== activeBranch) return false;
      if (!q) return true;
      return [p.id, p.name, p.born, p.died, p.spouse, p.spouseBorn, p.spouseDied, p.marriageDate]
        .join(' ').toLowerCase().includes(q);
    });

    rows.sort((a, b) => {
      let cmp;
      if (sortKey === 'id') {
        // Numeric dot-notation sort: compare each segment as a number so
        // 4 < 4.1 < 4.2 < 4.10 < 5 < 13 (not lexicographic order)
        const segsA = String(a.id || '').split('.').map(Number);
        const segsB = String(b.id || '').split('.').map(Number);
        cmp = 0;
        for (let i = 0; i < Math.max(segsA.length, segsB.length); i++) {
          const sa = isNaN(segsA[i]) ? -1 : (segsA[i] ?? -1);
          const sb = isNaN(segsB[i]) ? -1 : (segsB[i] ?? -1);
          if (sa !== sb) { cmp = sa - sb; break; }
        }
      } else {
        const av = String(a[sortKey] ?? '').toLowerCase();
        const bv = String(b[sortKey] ?? '').toLowerCase();
        cmp = av < bv ? -1 : av > bv ? 1 : 0;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });

    const totalRows  = rows.length;
    const totalPages = Math.ceil(totalRows / PER_PAGE) || 1;
    if (page > totalPages) page = totalPages;

    const rangeStart = totalRows === 0 ? 0 : (page - 1) * PER_PAGE + 1;
    const rangeEnd   = Math.min(page * PER_PAGE, totalRows);
    if (count) count.textContent = totalRows === 0 ? '0' : `${rangeStart}–${rangeEnd}`;
    if (total) total.textContent = totalRows;

    if (totalRows === 0) {
      table.innerHTML = '<tr><td colspan="8" class="mc-table__empty">No matches found.</td></tr>';
      renderPagination(pgEl, page, 1, () => {});
      return;
    }

    const pageRows = rows.slice((page - 1) * PER_PAGE, page * PER_PAGE);
    const cols = [
      { key: 'id',           render: p => `<span style="color:var(--ink-900)">${esc(p.id)}</span>` },
      { key: 'name',         render: p => {
          const depth = Math.max(0, (p.id || '').split('.').filter(Boolean).length - minSegs);
          return depth
            ? `<strong style="display:inline-block;padding-left:${depth * 20}px">${esc(p.name)}</strong>`
            : `<strong>${esc(p.name)}</strong>`;
      }},
      { key: 'born',         render: p => esc(p.born) },
      { key: 'died',         render: p => esc(p.died) },
      { key: 'spouse',       render: p => esc(p.spouse) },
      { key: 'spouseBorn',   render: p => esc(p.spouseBorn) },
      { key: 'spouseDied',   render: p => esc(p.spouseDied) },
      { key: 'marriageDate', render: p => esc(p.marriageDate) },
    ];

    table.innerHTML = pageRows.map(p =>
      `<tr>${cols.map(c =>
        `<td${SECTION_COLS.includes(c.key) ? ' class="mc-col--section"' : ''}>${c.render(p)}</td>`
      ).join('')}</tr>`
    ).join('');

    renderPagination(pgEl, page, totalPages, p => { page = p; render(); });
  }

  // Sort on header click
  document.querySelectorAll('.mc-sort-th').forEach(th => {
    th.addEventListener('click', () => {
      const key = th.dataset.sort;
      if (sortKey === key) sortDir = sortDir === 'asc' ? 'desc' : 'asc';
      else { sortKey = key; sortDir = 'asc'; }
      page = 1;
      document.querySelectorAll('.mc-sort-th .mc-sort-arrow').forEach(a => a.textContent = '');
      const arrow = th.querySelector('.mc-sort-arrow');
      if (arrow) arrow.textContent = sortDir === 'asc' ? '▲' : '▼';
      render();
    });
  });

  let gbSearchTimer;
  search?.addEventListener('input', () => {
    clearTimeout(gbSearchTimer);
    gbSearchTimer = setTimeout(() => { page = 1; render(); }, 150);
  });

  // Fetch data from the live Google Sheets CSV
  const dataUrl = window.MC_GB_DATA_URL;
  if (!dataUrl) {
    table.innerHTML = '<tr><td colspan="8" class="mc-table__empty">Green Book data source not configured.</td></tr>';
    return;
  }

  // Minimal CSV parser — handles quoted fields, CRLF, and empty cells
  function parseCSV(text) {
    const lines = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n');
    function splitRow(line) {
      const out = []; let cur = '', inQ = false;
      for (let i = 0; i < line.length; i++) {
        const c = line[i];
        if      (c === '"' && !inQ)                       { inQ = true; }
        else if (c === '"' &&  inQ && line[i + 1] === '"') { cur += '"'; i++; }
        else if (c === '"' &&  inQ)                       { inQ = false; }
        else if (c === ',' && !inQ)                       { out.push(cur.trim()); cur = ''; }
        else                                              { cur += c; }
      }
      out.push(cur.trim());
      return out;
    }
    return lines.filter(l => l.trim()).map(splitRow);
  }

  // Map CSV column headers → JS property keys
  const CSV_KEY_MAP = {
    'ID#':          'id',
    'Name':         'name',
    'Born':         'born',
    'Died':         'died',
    'Spouse':       'spouse',
    'Spouse Born':  'spouseBorn',
    'Spouse Died':  'spouseDied',
    'Marriage Date':'marriageDate',
  };

  fetch(dataUrl)
    .then(r => { if (!r.ok) throw new Error(r.status); return r.text(); })
    .then(text => {
      const rows = parseCSV(text.replace(/^﻿/, '')); // strip UTF-8 BOM Google Sheets sometimes prepends
      if (!rows.length) { gbData = []; render(); return; }
      const headers = rows[0];
      const keyMap  = headers.map(h => CSV_KEY_MAP[h] || null);
      gbData = rows.slice(1)
        .map(fields => {
          const obj = {};
          keyMap.forEach((key, i) => { if (key) obj[key] = fields[i] || ''; });
          return obj;
        })
        .filter(r => r.id || r.name); // skip blank rows
      minSegs = gbData.length
        ? Math.min(...gbData.map(p => (p.id || '').split('.').filter(Boolean).length))
        : 3;

      // Build branch jump nav from actual data
      const branchNav = document.getElementById('mc-gb-branch-nav');
      if (branchNav) {
        const branches = [...new Set(
          gbData.map(p => String(p.id || '').split('.')[0]).filter(Boolean)
        )].sort((a, b) => Number(a) - Number(b));

        branchNav.innerHTML = '';
        const label = document.createElement('span');
        label.className = 'mc-gb-branch-label';
        label.textContent = 'Branch';
        branchNav.appendChild(label);

        ['All', ...branches].forEach(b => {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'mc-gb-branch-pill' + (b === 'All' ? ' is-active' : '');
          btn.textContent = b;
          btn.addEventListener('click', () => {
            activeBranch = b === 'All' ? null : b;
            page = 1;
            branchNav.querySelectorAll('.mc-gb-branch-pill').forEach(p => p.classList.remove('is-active'));
            btn.classList.add('is-active');
            render();
          });
          branchNav.appendChild(btn);
        });

        branchNav.hidden = false;
      }

      render();
    })
    .catch(() => {
      table.innerHTML = '<tr><td colspan="8" class="mc-table__empty">Could not load Green Book data. Please try refreshing the page.</td></tr>';
    });
}

// ─── Green Book submission form ───────────────────────────────────────────────
function mcGreenBookForm() {
  const gbForm = document.getElementById('mc-gb-submit-form');
  if (!gbForm) return;
  gbForm.addEventListener('submit', e => {
    const errs = validateForm(gbForm, ['submitter', 'contact[email]']);
    // Validate first entry row's required fields
    const firstRow = gbForm.querySelector('.mc-gb-entry-row');
    if (firstRow) {
      ['entry_id', 'entry_name'].forEach(field => {
        const el = firstRow.querySelector(`[name$="[${field}]"]`);
        if (el && !el.value.trim()) errs[el.name] = 'Required';
      });
    }
    if (Object.keys(errs).length) {
      e.preventDefault();
      e.stopImmediatePropagation();
      showErrors(gbForm, errs);
      return;
    }
    // Build the formatted body field synchronously — Shopify's async recaptcha
    // callback calls form.submit() later, at which point this value is set.
    const get = n => gbForm.querySelector(`[name="${n}"]`)?.value.trim() || '';
    const typeEl = gbForm.querySelector('.mc-seg.is-on');
    const entries = [];
    gbForm.querySelectorAll('.mc-gb-entry-row').forEach((row, i) => {
      const rget = n => (row.querySelector(`[name$="[${n}]"]`)?.value || '').trim();
      const name = rget('entry_name');
      const id   = rget('entry_id');
      if (!name && !id) return;
      entries.push(
        `--- Entry ${i + 1} ---\n` +
        `ID#: ${id || 'Not provided'}\n` +
        `Name: ${name || 'Not provided'}\n` +
        `Born: ${rget('born') || 'Not provided'}\n` +
        `Died: ${rget('died') || 'Not provided'}\n` +
        `Spouse: ${rget('spouse') || 'Not provided'}\n` +
        `Spouse Born: ${rget('spouse_born') || 'Not provided'}\n` +
        `Spouse Died: ${rget('spouse_died') || 'Not provided'}\n` +
        `Marriage Date: ${rget('marriage_date') || 'Not provided'}`
      );
    });
    document.getElementById('mc-gb-body-hidden').value =
      `Green Book Submission\n\n` +
      `Type: ${typeEl ? typeEl.textContent.trim() : 'Update existing'}\n` +
      `Submitter: ${get('submitter')}\n\n` +
      (entries.join('\n\n') || '(no entries)') +
      `\n\nNotes:\n${get('notes') || 'None'}`;
    // valid — Shopify handles recaptcha + native submission; success shown via return_to redirect
  }, true);

  // Submit another change — reset form without closing modal
  const gbModal = document.getElementById('mc-gb-submit-modal');
  gbModal?.querySelector('[data-mc-gb-another]')?.addEventListener('click', () => {
    const successDiv = gbModal.querySelector('.mc-form-success');
    if (successDiv) successDiv.style.display = 'none';
    gbForm.style.display = '';
    gbForm.reset();
    const entryList = document.getElementById('mc-gb-entry-list');
    if (entryList) {
      Array.from(entryList.querySelectorAll('.mc-gb-entry-row')).slice(1).forEach(r => r.remove());
      entryList.querySelectorAll('input').forEach(el => el.value = '');
    }
  });
}

// ─── Business Directory filter ────────────────────────────────────────────────
function mcDirectory() {
  const grid   = document.getElementById('mc-biz-grid');
  const search = document.getElementById('mc-dir-search');
  const cats   = document.querySelectorAll('[data-mc-cat]');
  const pgEl   = document.getElementById('mc-dir-pagination');
  if (!grid) return;

  const PER_PAGE = 12;
  let activeCat = 'All', page = 1;

  const allCards   = Array.from(grid.querySelectorAll('.mc-biz'));
  const searchText = new Map(allCards.map(card => [card, card.textContent.toLowerCase()]));

  function render() {
    const q = (search?.value || '').trim().toLowerCase();

    const matching = allCards.filter(card => {
      const cat = card.dataset.category || '';
      return (activeCat === 'All' || cat === activeCat) && (!q || searchText.get(card).includes(q));
    });

    const totalPages = Math.ceil(matching.length / PER_PAGE) || 1;
    if (page > totalPages) page = totalPages;

    const pageSet = new Set(matching.slice((page - 1) * PER_PAGE, page * PER_PAGE));
    allCards.forEach(card => { card.style.display = pageSet.has(card) ? '' : 'none'; });

    renderPagination(pgEl, page, totalPages, p => { page = p; render(); });
  }

  cats.forEach(btn => {
    btn.addEventListener('click', () => {
      activeCat = btn.dataset.mcCat;
      page = 1;
      cats.forEach(b => b.classList.toggle('is-active', b.dataset.mcCat === activeCat));
      render();
    });
  });

  search?.addEventListener('input', () => { page = 1; render(); });

  // Business submission form
  const bizForm = document.getElementById('mc-biz-submit-form');
  if (bizForm) {
    bizForm.addEventListener('submit', e => {
      const errs = validateForm(bizForm, ['contact[business_name]', 'contact[owner]', 'contact[description]', 'contact[email]', 'contact[number]']);
      const numEl = bizForm.querySelector('[name="contact[number]"]');
      if (numEl && numEl.value.trim() && !/^\d+(\.\d+){2,5}$/.test(numEl.value.trim())) {
        errs['contact[number]'] = 'Must be 3–6 segments separated by dots (e.g. 7.5.7.2)';
      }
      const descEl = bizForm.querySelector('[name="contact[description]"]');
      if (descEl && descEl.value.trim().length > 360) {
        errs['contact[description]'] = 'Description must be 360 characters or fewer.';
      }
      if (Object.keys(errs).length) {
        e.preventDefault();
        e.stopImmediatePropagation();
        showErrors(bizForm, errs);
        return;
      }
      const get = name => bizForm.querySelector(`[name="${name}"]`)?.value.trim() || '';
      document.getElementById('mc-biz-body-hidden').value =
        `Business Directory Submission\n\n` +
        `Business: ${get('contact[business_name]')}\n` +
        `Owner: ${get('contact[owner]')}\n` +
        `Number: ${get('contact[number]') || 'Not provided'}\n` +
        `Category: ${get('contact[category]')}\n` +
        `Website: ${get('contact[website]') || 'Not provided'}\n` +
        `Phone: ${get('contact[phone]') || 'Not provided'}\n` +
        `Logo URL: ${get('contact[logo_url]') || 'Not provided'}\n\n` +
        `Description:\n${get('contact[description]')}`;
      // valid — Shopify handles recaptcha + native submission; success shown via return_to redirect
    }, true);

    // Live character counter for description
    const descField = bizForm.querySelector('#mc-biz-desc');
    const countEl   = document.getElementById('mc-biz-desc-count');
    if (descField && countEl) {
      descField.addEventListener('input', () => {
        const remaining = 360 - descField.value.length;
        const abs = Math.abs(remaining);
        countEl.textContent = remaining >= 0
          ? `${remaining} character${remaining === 1 ? '' : 's'} remaining`
          : `${abs} character${abs === 1 ? '' : 's'} over the limit`;
        countEl.style.color = remaining < 0 ? 'var(--wax-red)' : remaining < 40 ? 'var(--fg-2)' : 'var(--fg-3)';
      });
    }
  }

  render();

  // ── Card detail modal ──────────────────────────────────────────────────────
  function openBizDetail(card) {
    const d = card.dataset;

    // Mark: logo image or initials monogram
    const mark = document.getElementById('mc-biz-detail-mark');
    if (d.bizLogo) {
      const logoImg = document.createElement('img');
      logoImg.src = d.bizLogo;
      logoImg.alt = d.bizName || '';
      logoImg.width = 80;
      logoImg.height = 80;
      logoImg.loading = 'lazy';
      logoImg.style.cssText = 'width:80px;height:80px;object-fit:contain;';
      mark.innerHTML = '';
      mark.appendChild(logoImg);
      mark.className = 'mc-biz__mark mc-biz__mark--img';
    } else {
      mark.textContent = d.bizInitials || '';
      mark.className = 'mc-biz__mark';
    }

    // Header text
    document.getElementById('mc-biz-detail-name').textContent = d.bizName || '';
    const ownerEl = document.getElementById('mc-biz-detail-owner');
    ownerEl.textContent = d.bizNumber
      ? `${d.bizOwner || ''} · ${d.bizNumber}`
      : (d.bizOwner || '');

    // Category + description
    document.getElementById('mc-biz-detail-category').textContent = d.bizCategory || '';
    document.getElementById('mc-biz-detail-description').textContent = d.bizDescription || '';

    // Meta: website + phone as real links
    const meta = document.getElementById('mc-biz-detail-meta');
    meta.innerHTML = '';
    if (d.bizWebsite) {
      if (/^https?:\/\//i.test(d.bizWebsite)) {
        const a = document.createElement('a');
        a.href = d.bizWebsite;
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
        a.style.color = 'var(--green-700)';
        a.textContent = d.bizWebsiteDisplay || d.bizWebsite;
        meta.appendChild(a);
      } else {
        const span = document.createElement('span');
        span.textContent = d.bizWebsiteDisplay || d.bizWebsite;
        meta.appendChild(span);
      }
    }
    if (d.bizPhone) {
      if (d.bizWebsite) {
        const sep = document.createElement('span');
        sep.textContent = ' · ';
        meta.appendChild(sep);
      }
      const tel = document.createElement('a');
      tel.href = `tel:${d.bizPhone.replace(/\D/g, '')}`;
      tel.textContent = d.bizPhone;
      meta.appendChild(tel);
    }

    mcOpenModal('mc-biz-detail-modal');
  }

  grid.addEventListener('click', e => {
    if (e.target.closest('a')) return; // let website links navigate normally
    const card = e.target.closest('.mc-biz[data-biz-name]');
    if (card) openBizDetail(card);
  });

  grid.addEventListener('keydown', e => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    const card = e.target.closest('.mc-biz[data-biz-name]');
    if (card) { e.preventDefault(); openBizDetail(card); }
  });
}

// ─── Cart drawer (Shopify AJAX) ───────────────────────────────────────────────
function mcCart() {
  const drawer  = document.getElementById('mc-cart-drawer');
  const overlay = document.getElementById('mc-cart-overlay');
  const countEl = document.getElementById('mc-cart-count');

  function openCart() {
    if (!drawer) return;
    drawer.classList.add('is-open');
    if (overlay) overlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    refreshCart();
  }

  function closeCart() {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    if (overlay) overlay.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  window.mcCloseCart = closeCart;
  window.mcOpenCart  = openCart;

  overlay?.addEventListener('click', closeCart);
  drawer?.querySelector('[data-mc-close="mc-cart-drawer"]')?.addEventListener('click', closeCart);
  document.querySelectorAll('[data-mc-open="mc-cart-drawer"]').forEach(btn =>
    btn.addEventListener('click', openCart)
  );

  async function refreshCart() {
    const body = drawer?.querySelector('.mc-cart-drawer__items');
    const foot = drawer?.querySelector('.mc-cart-drawer__foot');
    if (!body) return;

    let cart;
    try {
      const res = await fetch('/cart.js');
      cart = await res.json();
    } catch (e) {
      return; // network error — leave drawer as-is
    }

    if (countEl) countEl.textContent = cart.item_count > 0 ? cart.item_count : '';

    if (cart.item_count === 0) {
      body.innerHTML = '<div class="mc-cart-empty"><p>Your cart is empty.</p></div>';
      if (foot) foot.style.display = 'none';
      return;
    }

    if (foot) foot.style.display = '';

    const escA    = s => String(s || '').replace(/</g,'&lt;').replace(/"/g,'&quot;');
    const mcCrest = window.mcCrestUrl || '';
    body.innerHTML = cart.items.map(item => {
      const title      = String(item.product_title || '').replace(/</g,'&lt;');
      const variant    = (item.variant_title && item.variant_title !== 'Default Title')
        ? item.variant_title.replace(/</g,'&lt;') : '';
      const hasImg     = Boolean(item.image);
      const img        = escA(hasImg ? item.image : mcCrest);
      const imgStyle   = hasImg
        ? 'width:100%;height:100%;object-fit:cover;display:block'
        : 'width:100%;height:100%;object-fit:contain;padding:10px;display:block;background:var(--green-800)';
      return `<div class="mc-cart-item" data-key="${escA(item.key)}">
        <a href="${escA(item.url)}" class="mc-cart-item__media">
          <img src="${img}" alt="${hasImg ? escA(item.product_title) : ''}" width="64" height="64" style="${imgStyle}">
        </a>
        <div>
          <h4 class="mc-cart-item__name">${title}</h4>
          <div class="mc-cart-item__meta">${variant}</div>
          <div class="mc-cart-item__qty">
            <button type="button" data-qty-change="-1" data-key="${item.key}">−</button>
            <span style="font-family:var(--font-mono);font-size:13px;min-width:20px;text-align:center">${item.quantity}</span>
            <button type="button" data-qty-change="1" data-key="${item.key}">+</button>
          </div>
        </div>
        <div style="display:flex;flex-direction:column;align-items:flex-end;gap:6px">
          <span class="mc-cart-item__price">${formatMoney(item.line_price)}</span>
          <button type="button" class="mc-cart-item__remove" data-key="${item.key}" data-qty-change="-999">Remove</button>
        </div>
      </div>`;
    }).join('');

    const totalEl = foot?.querySelector('.mc-cart-drawer__total span:last-child');
    if (totalEl) totalEl.textContent = formatMoney(cart.total_price);

    // Qty change handlers
    body.querySelectorAll('[data-qty-change]').forEach(btn => {
      btn.addEventListener('click', async () => {
        const key    = btn.dataset.key;
        const delta  = parseInt(btn.dataset.qtyChange, 10);
        const item   = cart.items.find(i => i.key === key);
        if (!item) return;
        const newQty = Math.max(0, item.quantity + delta);
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity: newQty }),
        });
        refreshCart();
      });
    });
  }

  // Refresh count on page load
  fetch('/cart.js').then(r => r.json()).then(cart => {
    if (countEl && cart.item_count > 0) countEl.textContent = cart.item_count;
  });
}

// ─── Utility helpers ──────────────────────────────────────────────────────────
function validateForm(form, fields) {
  const errs = {};
  fields.forEach(name => {
    const el = form.querySelector(`[name="${name}"], #${name}`);
    if (!el) return;
    const val = el.value.trim();
    if (!val) { errs[name] = 'Required'; return; }
    if (el.type === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) errs[name] = 'Please enter a valid email.';
    if ((el.name === 'body' || el.id === 'biz-body') && val.length < 20) errs[name] = 'Please write at least 20 characters.';
  });
  return errs;
}

function showErrors(form, errs) {
  form.querySelectorAll('.mc-field__err').forEach(e => e.remove());
  form.querySelectorAll('.mc-field--error').forEach(e => e.classList.remove('mc-field--error'));
  Object.entries(errs).forEach(([name, msg]) => {
    const el    = form.querySelector(`[name="${name}"], #${name}`);
    const field = el?.closest('.mc-field');
    if (field) {
      field.classList.add('mc-field--error');
      const span = document.createElement('span');
      span.className = 'mc-field__err';
      span.textContent = msg;
      field.appendChild(span);
    }
  });
  const first = Object.keys(errs)[0];
  form.querySelector(`[name="${first}"], #${first}`)?.focus();
}

function formatMoney(cents) {
  return '$' + (cents / 100).toFixed(2);
}

function renderPagination(container, page, totalPages, onPageChange) {
  if (!container) return;
  if (totalPages <= 1) { container.innerHTML = ''; return; }

  // Build windowed page list: always first & last, current ±2, ellipsis for gaps
  const pages = [];
  for (let i = 1; i <= totalPages; i++) {
    if (i === 1 || i === totalPages || (i >= page - 2 && i <= page + 2)) {
      pages.push(i);
    } else if (pages[pages.length - 1] !== '…') {
      pages.push('…');
    }
  }

  const numBtns = pages.map(p =>
    p === '…'
      ? `<span class="mc-pg-ellipsis">…</span>`
      : `<button class="mc-pg-num${p === page ? ' is-active' : ''}" data-pg="${p}">${p}</button>`
  ).join('');

  container.innerHTML = `
    <div class="mc-pg-row">
      <button class="mc-pg-btn" ${page === 1 ? 'disabled' : ''} data-pg="${page - 1}">&larr; Prev</button>
      <span class="mc-pg-info">Page ${page} of ${totalPages}</span>
      <button class="mc-pg-btn" ${page === totalPages ? 'disabled' : ''} data-pg="${page + 1}">Next &rarr;</button>
    </div>
    <div class="mc-pg-nums">${numBtns}</div>
  `;

  container.querySelectorAll('[data-pg]').forEach(btn => {
    btn.addEventListener('click', () => onPageChange(Number(btn.dataset.pg)));
  });
}

// ─── Smooth scroll for event nav ─────────────────────────────────────────────
function mcEventNav() {
  document.querySelectorAll('[data-mc-scroll]').forEach(a => {
    a.addEventListener('click', e => {
      e.preventDefault();
      const target = document.getElementById(a.dataset.mcScroll);
      if (target) {
        const top = target.getBoundingClientRect().top + window.pageYOffset - 100;
        window.scrollTo({ top, behavior: 'smooth' });
      }
    });
  });
}

// ─── Events jump nav arrows ───────────────────────────────────────────────────
function mcJumpNav() {
  const wrap = document.querySelector('.mc-jump-nav-wrap');
  if (!wrap) return;
  const nav  = wrap.querySelector('.mc-jump-nav');
  const prev = wrap.querySelector('.mc-jump-nav__btn--prev');
  const next = wrap.querySelector('.mc-jump-nav__btn--next');
  if (!nav || !prev || !next) return;

  function update() {
    prev.hidden = nav.scrollLeft <= 1;
    next.hidden = nav.scrollLeft >= nav.scrollWidth - nav.clientWidth - 1;
  }

  prev.addEventListener('click', () => nav.scrollBy({ left: -220, behavior: 'smooth' }));
  next.addEventListener('click', () => nav.scrollBy({ left:  220, behavior: 'smooth' }));
  nav.addEventListener('scroll', update, { passive: true });
  update();
}

// ─── Segmented controls ────────────────────────────────────────────────────────
function mcSegmented() {
  document.querySelectorAll('.mc-segmented').forEach(group => {
    group.querySelectorAll('.mc-seg').forEach(btn => {
      btn.addEventListener('click', () => {
        group.querySelectorAll('.mc-seg').forEach(b => b.classList.remove('is-on'));
        btn.classList.add('is-on');
        const input = group.parentElement.querySelector('input[type="hidden"]');
        if (input) input.value = btn.textContent.trim();
      });
    });
  });
}

// ─── Green Book — dynamic entry rows ─────────────────────────────────────────
function mcGreenBookEntries() {
  document.querySelectorAll('[data-mc-add-gb-entry]').forEach(btn => {
    btn.addEventListener('click', () => {
      const list = document.getElementById(btn.dataset.mcAddGbEntry);
      if (!list) return;
      const idx = list.querySelectorAll('.mc-gb-entry-row').length;
      const row = document.createElement('div');
      row.className = 'mc-gb-entry-row';
      row.style.cssText = 'background:var(--cream);border:1px solid var(--border-1);padding:16px;margin-bottom:8px;display:flex;flex-direction:column;gap:12px';
      row.innerHTML = `
        <div style="display:flex;justify-content:space-between;align-items:center">
          <div class="mc-eyebrow" style="font-size:10px">Person ${idx + 1}</div>
          <button type="button" class="mc-btn mc-btn--ghost mc-btn--sm" onclick="this.closest('.mc-gb-entry-row').remove()">Remove</button>
        </div>
        <div class="mc-field-row">
          <div class="mc-field"><label>ID# <span class="mc-req" aria-hidden="true">*</span></label><input name="entries[${idx}][entry_id]" placeholder="e.g. 1.3.2" autocomplete="off"></div>
          <div class="mc-field"><label>Name <span class="mc-req" aria-hidden="true">*</span></label><input name="entries[${idx}][entry_name]" placeholder="Mary McCarthy"></div>
        </div>
        <div class="mc-field-row">
          <div class="mc-field"><label>Born</label><input name="entries[${idx}][born]" placeholder="MM/DD/YYYY"></div>
          <div class="mc-field"><label>Died</label><input name="entries[${idx}][died]" placeholder="MM/DD/YYYY or leave blank"></div>
        </div>
        <div class="mc-field-row">
          <div class="mc-field"><label>Spouse</label><input name="entries[${idx}][spouse]" placeholder="Full name"></div>
          <div class="mc-field"><label>Marriage Date</label><input name="entries[${idx}][marriage_date]" placeholder="MM/DD/YYYY"></div>
        </div>
        <div class="mc-field-row">
          <div class="mc-field"><label>Spouse Born</label><input name="entries[${idx}][spouse_born]" placeholder="MM/DD/YYYY"></div>
          <div class="mc-field"><label>Spouse Died</label><input name="entries[${idx}][spouse_died]" placeholder="MM/DD/YYYY or leave blank"></div>
        </div>`;
      list.appendChild(row);
    });
  });
}

// ─── Kids form — dynamic child rows ──────────────────────────────────────────
function mcKidsForms() {
  document.querySelectorAll('[data-mc-add-kid]').forEach(btn => {
    btn.addEventListener('click', () => {
      const list = document.getElementById(btn.dataset.mcAddKid);
      if (!list) return;
      const idx = list.querySelectorAll('.mc-kid-row').length;
      const row = document.createElement('div');
      row.className = 'mc-kid-row';
      row.style.cssText = 'display:grid;grid-template-columns:2fr 1fr auto;gap:12px;align-items:end;padding:12px;background:var(--cream);border:1px solid var(--border-1);margin-bottom:8px';
      row.innerHTML = `
        <div class="mc-field"><label>Child's name</label><input name="kids[${idx}][name]" placeholder="Maeve McCarthy"></div>
        <div class="mc-field"><label>Age</label><input type="number" name="kids[${idx}][age]" placeholder="8"></div>
        <button type="button" class="mc-btn mc-btn--ghost mc-btn--sm" style="align-self:end" onclick="this.closest('.mc-kid-row').remove()">Remove</button>`;
      list.appendChild(row);
    });
  });
}

// ─── Golf form — dynamic golfer rows (max 4) ─────────────────────────────────
function mcGolferRows() {
  document.querySelectorAll('[data-mc-add-golfer]').forEach(btn => {
    const list = document.getElementById(btn.dataset.mcAddGolfer);
    if (!list) return;

    function syncBtn() {
      const count = list.querySelectorAll('.mc-golfer-row').length;
      btn.disabled    = count >= 4;
      btn.textContent = count >= 4 ? '+ Add golfer (4 max)' : '+ Add another golfer';
    }

    function bindRemove(row) {
      row.querySelector('.mc-remove-golfer')?.addEventListener('click', () => {
        row.remove();
        syncBtn();
      });
    }

    list.querySelectorAll('.mc-golfer-row').forEach(bindRemove);

    btn.addEventListener('click', () => {
      const idx = list.querySelectorAll('.mc-golfer-row').length;
      if (idx >= 4) return;
      const row = document.createElement('div');
      row.className = 'mc-golfer-row';
      row.style.cssText = 'background:var(--cream);border:1px solid var(--border-1);padding:12px;margin-bottom:8px;display:flex;flex-direction:column;gap:12px';
      row.innerHTML = `
        <div style="display:flex;justify-content:space-between;align-items:center">
          <div class="mc-eyebrow" style="font-size:10px;margin-bottom:0">Golfer ${idx + 1}</div>
          <button type="button" class="mc-btn mc-btn--ghost mc-btn--sm mc-remove-golfer">Remove</button>
        </div>
        <div class="mc-field-row">
          <div class="mc-field"><label>First name</label><input name="golfers[${idx}][firstName]" placeholder="James"></div>
          <div class="mc-field"><label>Last name</label><input name="golfers[${idx}][lastName]" placeholder="McCarthy"></div>
        </div>
        <div class="mc-field"><label>Handicap</label><input name="golfers[${idx}][handicap]" placeholder="e.g. 14 or scratch"></div>`;
      bindRemove(row);
      list.appendChild(row);
      syncBtn();
    });
  });
}

// ─── Cart-based event registration (banquet, golf, kids) ─────────────────────
function mcCartForms() {
  document.querySelectorAll('.mc-cart-form').forEach(form => {
    form.addEventListener('submit', async e => {
      e.preventDefault();

      const cartType = form.dataset.cartType;
      const variants = window.MC_EVENT_VARIANTS || {};
      const get      = n => (form.querySelector(`[name="${n}"]`)?.value || '').trim();

      // Validate required fields
      const reqNames = Array.from(form.querySelectorAll('[required]')).map(el => el.name).filter(Boolean);
      const errs     = validateForm(form, reqNames);
      if (Object.keys(errs).length) { showErrors(form, errs); return; }

      // Build items array
      let items = [];

      if (cartType === 'banquet') {
        const vid      = parseInt(variants.banquet_adult, 10);
        const cid      = parseInt(variants.banquet_child, 10);
        const adults   = Math.max(1, parseInt(get('adults')   || '1', 10));
        const children = Math.max(0, parseInt(get('children') || '0', 10));
        const name     = `${get('firstName')} ${get('lastName')}`.trim();
        const props    = { 'Name': name, 'Email': get('contact[email]') };
        const idNum    = get('family_id');
        if (idNum) props['ID#']   = idNum;
        const notes    = get('notes');
        if (notes) props['Notes'] = notes;
        if (vid && adults   > 0) items.push({ id: vid, quantity: adults,   properties: props });
        if (cid && children > 0) items.push({ id: cid, quantity: children, properties: { 'Name': name, 'Email': props['Email'] } });

      } else if (cartType === 'golf') {
        const vid      = parseInt(variants.golf, 10);
        const golfers  = [];
        form.querySelectorAll('.mc-golfer-row').forEach((row, i) => {
          const fn = (row.querySelector('[name$="[firstName]"]')?.value || '').trim();
          const ln = (row.querySelector('[name$="[lastName]"]')?.value  || '').trim();
          const hc = (row.querySelector('[name$="[handicap]"]')?.value  || '').trim();
          if (fn || ln) golfers.push(`${i + 1}. ${fn} ${ln}${hc ? ' (hdcp: ' + hc + ')' : ''}`.trim());
        });
        if (vid && golfers.length > 0) {
          items.push({
            id: vid,
            quantity: golfers.length,
            properties: {
              'Contact':             `${get('firstName')} ${get('lastName')}`.trim(),
              'Email':               get('contact[email]'),
              'Golfer(s)':          golfers.join(' / '),
              'Foursome preference': get('foursome') || 'None',
            }
          });
        }

      } else if (cartType === 'kids') {
        const vid  = parseInt(variants.kids, 10);
        const kids = [];
        form.querySelectorAll('.mc-kid-row').forEach(row => {
          const name = (row.querySelector('[name$="[name]"]')?.value || '').trim();
          const age  = (row.querySelector('[name$="[age]"]')?.value  || '').trim();
          if (name) kids.push(`${name}${age ? ' age ' + age : ''}`);
        });
        if (vid && kids.length > 0) {
          const props = {
            'Parent':   `${get('firstName')} ${get('lastName')}`.trim(),
            'Email':    get('contact[email]'),
            'Phone':    get('phone'),
            'Children': kids.join('; '),
          };
          const idNum = get('family_id');
          if (idNum) props['ID#'] = idNum;
          items.push({ id: vid, quantity: kids.length, properties: props });
        }
      }

      // Variant IDs not yet configured
      if (!items.length) {
        let notice = form.querySelector('.mc-cart-notice');
        if (!notice) {
          notice = document.createElement('p');
          notice.className = 'mc-cart-notice';
          notice.style.cssText = 'background:var(--bg-soft);border:1px solid var(--border-1);padding:12px;font-family:var(--font-body);font-size:13px;color:var(--fg-2);text-align:center;border-radius:4px;margin-top:8px';
          form.appendChild(notice);
        }
        notice.textContent = 'Online registration is not yet open for this event. Email mccarthy.famreunion@gmail.com to reserve your spot.';
        return;
      }

      const submitBtn = form.querySelector('[type="submit"]');
      if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = 'Adding…'; }

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items }),
        });
        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          throw new Error(errData.description || 'Could not add to cart');
        }

        // Refresh header cart count
        const cartRes  = await fetch('/cart.js');
        const cartData = await cartRes.json();
        const countEl  = document.getElementById('mc-cart-count');
        if (countEl) countEl.textContent = cartData.item_count > 0 ? cartData.item_count : '';

        // Show success state
        const modal      = form.closest('.mc-modal-veil');
        const formWrap   = modal?.querySelector('.mc-form-wrap');
        const successDiv = modal?.querySelector('.mc-form-success');
        if (formWrap)   formWrap.style.display   = 'none';
        if (successDiv) successDiv.style.display = 'block';

      } catch (err) {
        if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = 'Add to cart'; }
        let errEl = form.querySelector('.mc-cart-err');
        if (!errEl) {
          errEl = document.createElement('p');
          errEl.className = 'mc-cart-err';
          errEl.style.cssText = 'color:var(--wax-red);font-size:14px;text-align:center;margin:8px 0';
          submitBtn ? submitBtn.before(errEl) : form.appendChild(errEl);
        }
        errEl.textContent = err.message || 'Something went wrong. Please try again or email mccarthy.famreunion@gmail.com.';
      }
    });
  });
}

// ─── Shop page category filter ────────────────────────────────────────────────
function mcShop() {
  const cats = document.querySelectorAll('[data-mc-shop-cat]');
  if (!cats.length) return;
  let active = 'All';

  cats.forEach(btn => {
    btn.addEventListener('click', () => {
      active = btn.dataset.mcShopCat;
      cats.forEach(b => b.classList.toggle('is-active', b.dataset.mcShopCat === active));
      let visible = 0;
      document.querySelectorAll('[data-product-type]').forEach(card => {
        const show = active === 'All' || card.dataset.productType === active;
        card.style.display = show ? '' : 'none';
        if (show) visible++;
      });
      const countEl = document.getElementById('mc-shop-count');
      if (countEl) countEl.textContent = visible + ' item' + (visible !== 1 ? 's' : '');
    });
  });
}

// ─── Post-submit success detection ────────────────────────────────────────────
function mcCheckFormSuccess() {
  const params = new URLSearchParams(location.search);
  const success = params.get('mc_success');
  if (!success) return;

  params.delete('mc_success');
  const qs = params.toString();
  history.replaceState(null, '', location.pathname + (qs ? '?' + qs : '') + location.hash);

  // Handle event registration success (e.g. mc_success=event-golf)
  if (success.startsWith('event-')) {
    const blockId = success.slice(6);
    const modal = document.getElementById('mc-reg-' + blockId);
    if (!modal) return;
    const formWrap   = modal.querySelector('.mc-form-wrap');
    const successDiv = modal.querySelector('.mc-form-success');
    if (formWrap)   formWrap.style.display = 'none';
    if (successDiv) successDiv.style.display = 'block';
    mcOpenModal('mc-reg-' + blockId);
    return;
  }

  const map = {
    contact:   { modal: 'mc-contact-modal',   form: 'mc-contact-form',   sent: 'mc-contact-sent' },
    greenbook: { modal: 'mc-gb-submit-modal',  form: 'mc-gb-submit-form', sent: null },
    directory: { modal: 'mc-biz-submit-modal', form: 'mc-biz-submit-form', sent: null },
  };
  const cfg = map[success];
  if (!cfg) return;

  const form  = document.getElementById(cfg.form);
  const modal = document.getElementById(cfg.modal);
  if (!form || !modal) return;

  const successDiv = cfg.sent
    ? document.getElementById(cfg.sent)
    : modal.querySelector('.mc-form-success');
  if (successDiv) { form.style.display = 'none'; successDiv.style.display = 'block'; }
  mcOpenModal(cfg.modal);
}

// ─── Init ─────────────────────────────────────────────────────────────────────
document.addEventListener('DOMContentLoaded', () => {
  mcCountdown();
  mcHeaderScroll();
  mcMobileNav();
  mcModals();
  mcContactForm();
  mcEventForms();
  mcGreenBook();
  mcGreenBookForm();
  mcDirectory();
  mcCart();
  mcEventNav();
  mcJumpNav();
  mcSegmented();
  mcGreenBookEntries();
  mcKidsForms();
  mcGolferRows();
  mcCartForms();
  mcShop();
  mcCheckFormSuccess();
});
